import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Avatar,
  Paper,
  Stack,
  Fade,
  Grow
} from '@mui/material';
import {
  People,
  BookOnline,
  AttachMoney,
  HourglassEmpty,
  Visibility,
  Add,
  Inventory,
  Analytics,
  TrendingUp,
  StarBorder,
  Today
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../hooks';
import { dashboardService } from '../services';
import { LoadingSpinner, StatsCard, StatusChip, PageHeader } from './ui';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];


function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBookings: 0,
    totalRevenue: 0,
    pendingBookings: 0
  });
  const [chartData, setChartData] = useState([]);
  const [popularPackages, setPopularPackages] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [bookingTrends, setBookingTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { showToast } = useToast();
  const { goToRoute } = useNavigation();

  useEffect(() => {
    loadDashboardData();
    // Set up auto-refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setError('');
      
      // Load dashboard stats
      const statsData = await dashboardService.getStats();
      setStats(statsData);

      // Load chart data for revenue analytics
      const revenueData = await dashboardService.getRevenueAnalytics({ 
        period: '30d' 
      });
      setChartData(revenueData.chartData || []);

      // Load popular packages
      const packagesData = await dashboardService.getPopularPackages({ 
        limit: 5 
      });
      setPopularPackages(packagesData.packages || []);

      // Load recent activities
      const activitiesData = await dashboardService.getRecentActivities({ 
        limit: 10 
      });
      setRecentActivities(activitiesData.activities || []);

      // Load booking trends
      const trendsData = await dashboardService.getBookingTrends({ 
        period: '7d' 
      });
      setBookingTrends(trendsData.trends || []);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Failed to load dashboard data. Please try refreshing the page.');
      showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinner 
        size={60}
        message="Loading dashboard..."
      />
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome back, ${user?.name || user?.email || 'Administrator'}! Here's an overview of your business performance.`}
        breadcrumbs={[]}
        actions={[
          {
            label: 'Refresh Data',
            icon: <Analytics />,
            onClick: loadDashboardData,
            variant: 'outlined'
          }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {console.log("stats", stats)}
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Users"
            value={stats?.data?.overview?.total_users || '0'}
            icon={People}
            color="#6366f1"
            trend={stats?.data?.new_users_month || 0}
            subtitle={`${stats?.data?.new_users_today || 0} new today`}
            gradient
            onClick={() => goToRoute('/users')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Bookings"
            value={stats?.data?.overview?.total_bookings || '0'}
            icon={BookOnline}
            color="#10b981"
            trend={stats?.data?.growth_metrics?.booking_growth || 0}
            subtitle={`${stats?.data?.detailed_statistics?.bookings?.today_bookings || 0} new today`}
            gradient
            onClick={() => goToRoute('/bookings')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Revenue"
            value={`RM ${stats?.data?.overview?.total_revenue || '0'}`}
            icon={AttachMoney}
            color="#3b82f6"
            trend={stats?.data?.growth_metrics?.revenue_growth || 0}
            subtitle={`RM ${stats?.data?.detailed_statistics?.revenue?.today_revenue || 0} today`}
            gradient
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Pending Bookings"
            value={stats?.data?.detailed_statistics?.bookings?.pending_bookings || '0'}
            icon={HourglassEmpty}
            color="#f59e0b"
            subtitle="Require attention"
            gradient
            onClick={() => goToRoute('/bookings?status=pending')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={400}>
            <Card sx={{ height: 420 }}>
              <CardContent sx={{ p: 3, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Revenue Analytics
                  </Typography>
                  <StatusChip status="active" showIcon={false} customLabel="Last 30 Days" />
                </Box>
                
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#64748b' }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#64748b' }}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [`RM ${value?.toLocaleString()}`, 'Revenue']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#6366f1"
                        strokeWidth={3}
                        fill="url(#colorRevenue)"
                        dot={{ fill: '#6366f1', r: 4 }}
                        activeDot={{ r: 6, fill: '#6366f1' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={340}>
                    <Box textAlign="center">
                      <Analytics sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">
                        No revenue data available
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Booking Trends */}
        <Grid item xs={12} lg={4}>
          <Grow in timeout={500}>
            <Card sx={{ height: 420 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Booking Status Distribution
                </Typography>
                {bookingTrends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={340}>
                    <PieChart>
                      <Pie
                        data={bookingTrends}
                        cx="50%"
                        cy="45%"
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {bookingTrends.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value, name) => [value, name]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '14px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height={340}>
                    <Box textAlign="center">
                      <BookOnline sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">
                        No booking data available
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grow>
        </Grid>

        {/* Popular Packages */}
        <Grid item xs={12} md={6}>
          <Fade in timeout={600}>
            <Card sx={{ height: 420 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Popular Packages
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => goToRoute('/packages')}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    View All
                  </Button>
                </Box>
                
                <List sx={{ maxHeight: 350, overflow: 'auto' }}>
                  {popularPackages.length > 0 ? (
                    popularPackages.map((pkg, index) => (
                      <ListItem 
                        key={pkg.id} 
                        divider={index < popularPackages.length - 1}
                        sx={{
                          px: 0,
                          py: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderRadius: 1
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{ 
                              bgcolor: COLORS[index % COLORS.length],
                              width: 40,
                              height: 40,
                              fontWeight: 600
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {pkg.name}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                {pkg.bookingCount || 0} bookings
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                RM {pkg.price?.toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Stack spacing={1} alignItems="flex-end">
                            <StatusChip
                              status="active"
                              showIcon={false}
                              customLabel={pkg.category || 'Uncategorized'}
                              size="small"
                              variant="outlined"
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <StarBorder sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">
                                {(Math.random() * 2 + 3).toFixed(1)}
                              </Typography>
                            </Box>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))
                  ) : (
                    <Box textAlign="center" py={6}>
                      <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" variant="body2">
                        No package data available
                      </Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Fade in timeout={700}>
            <Card sx={{ height: 420 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Recent Activities
                </Typography>
                
                <List sx={{ maxHeight: 350, overflow: 'auto' }}>
                  {recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => (
                      <ListItem 
                        key={index} 
                        divider={index < recentActivities.length - 1}
                        sx={{
                          px: 0,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderRadius: 1
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{ 
                              bgcolor: getActivityColor(activity.type), 
                              width: 36, 
                              height: 36 
                            }}
                          >
                            {getActivityIcon(activity.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {activity.description}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {new Date(activity.timestamp).toLocaleString()}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <StatusChip
                            status={activity.type}
                            size="small"
                            variant="outlined"
                            showIcon={false}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))
                  ) : (
                    <Box textAlign="center" py={6}>
                      <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" variant="body2">
                        No recent activities
                      </Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Fade in timeout={800}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<Add />}
                      onClick={() => goToRoute('/packages')}
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      Add Package
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<Visibility />}
                      onClick={() => goToRoute('/bookings')}
                      sx={{
                        py: 1.5,
                        borderColor: '#10b981',
                        color: '#10b981',
                        '&:hover': {
                          borderColor: '#059669',
                          backgroundColor: '#10b98110',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      View Bookings
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<People />}
                      onClick={() => goToRoute('/users')}
                      sx={{
                        py: 1.5,
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        '&:hover': {
                          borderColor: '#2563eb',
                          backgroundColor: '#3b82f610',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Manage Users
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<Analytics />}
                      onClick={() => goToRoute('/deals')}
                      sx={{
                        py: 1.5,
                        borderColor: '#f59e0b',
                        color: '#f59e0b',
                        '&:hover': {
                          borderColor: '#d97706',
                          backgroundColor: '#f59e0b10',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      View Deals
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
}

// Helper functions
const getActivityColor = (type) => {
  switch (type) {
    case 'booking': return 'success.main';
    case 'user': return 'primary.main';
    case 'payment': return 'info.main';
    case 'package': return 'warning.main';
    default: return 'grey.500';
  }
};

const getActivityIcon = (type) => {
  switch (type) {
    case 'booking': return <BookOnline sx={{ fontSize: 16 }} />;
    case 'user': return <People sx={{ fontSize: 16 }} />;
    case 'payment': return <AttachMoney sx={{ fontSize: 16 }} />;
    case 'package': return <Inventory sx={{ fontSize: 16 }} />;
    default: return <Today sx={{ fontSize: 16 }} />;
  }
};

export default Dashboard;